// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  Autocomplete,
  type AutocompleteRenderOptionState,
  Button,
  CircularProgress,
  Paper,
  type PaperProps,
  Tooltip,
} from '@mui/material';
import {
  AppIcon,
  AssetBrand,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';
import { useAppNavigate } from '@advisor-app/hooks';
import { useTranslation } from 'react-i18next';
import {
  type Dispatch,
  type FC,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactElement,
  type SetStateAction,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  VariableSizeList as List,
  type ListChildComponentProps,
} from 'react-window';
import { type AssetsSearchResp } from '@shared/data-access/interfaces/api/ai/assets';
import { classNames } from '@shared/ui-kit/utils/classnames';
import {
  selectComparisonTickers,
  setComparisonTickers,
} from '@advisor-app/store/slices/my-research';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import { useConst, useDebounce } from '@shared/ui-kit/hooks';
import { useInvestmentPopup } from '@shared/tag-management/components/investment-popup';
import { AssetOperationModal } from '@shared/portfolio-operations';
import type {
  SourceAsset,
  BuyOperationData,
} from '@shared/portfolio-operations/types';
import { useSendToTrader } from '@advisor-app/features/operations';
import { useAdvisorRole } from '@advisor-app/hooks/use-advisor-role';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { OperationType } from '@shared/data-access/interfaces/operation-type';

type ItemData = [
  HTMLAttributes<HTMLLIElement>,
  AssetsSearchResp,
  AutocompleteRenderOptionState,
  (ticker: string) => void,
  boolean,
  ((option: AssetsSearchResp) => void) | undefined,
];

type RowProps = ListChildComponentProps<Array<ItemData>>;

const Row: FC<RowProps> = ({ data, index, style }) => {
  const { t } = useTranslation();

  const { openInvestmentPopup } = useInvestmentPopup();

  const dataSet = data[index];

  const props = dataSet[0];

  const option = dataSet[1];

  const onCompare = dataSet[3];

  const disabled = dataSet[4];

  const onBuy = dataSet[5];

  return (
    <li
      {...props}
      key={index}
      style={style}
      className={classNames(props.className, 'items-center')}
      onClick={e => e.preventDefault()}
    >
      <div className="flex w-full items-center justify-between text-base">
        <AssetBrand
          ticker={option.ticker}
          assetType={option.asset_type}
          logoUrl={option.url_logo}
          label={`${option.ticker} - ${option.name}`}
          url={option.url_morningstar}
          analyzePopup={openInvestmentPopup}
          watchlistButton={
            <WatchlistToggleButton
              ticker={option.ticker}
              assetType={option.asset_type}
            />
          }
        />
        <div className="flex items-center gap-x-2">
          {option.asset_type !== 'stock' && (
            <Tooltip
              title={
                /* istanbul ignore next */ disabled
                  ? t('common.already-added-to-comparison', { ns: 'common' })
                  : t('common.add-to-comparison', { ns: 'common' })
              }
            >
              <span>
                <Button
                  type="button"
                  className="!rounded-3xl"
                  variant="contained"
                  color="secondary"
                  size="small"
                  data-testid={`compare-button-${option.ticker}`}
                  disabled={disabled}
                  onClick={() => onCompare(option.ticker)}
                >
                  {t('common.compare', { ns: 'common' })}
                </Button>
              </span>
            </Tooltip>
          )}
          {onBuy ? (
            <Tooltip title={t('research.add-to-trades', { ns: 'research' })}>
              <span>
                <Button
                  type="button"
                  className="!rounded-3xl"
                  variant="contained"
                  color="secondary"
                  size="small"
                  data-testid={`buy-button-${option.ticker}`}
                  onClick={() => onBuy(option)}
                >
                  {t('common.buy', { ns: 'common' })}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </li>
  );
};

const OuterElementContext = createContext({});

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext);

  return <div ref={ref} {...props} {...outerProps} />;
});
/* istanbul ignore next */
const StateContext = createContext<[string, Dispatch<SetStateAction<string>>]>([
  '', // eslint-disable-next-line @typescript-eslint/no-empty-function
  () => {},
]);

const ListboxComponent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(({ children, ...props }, ref) => {
  const ITEM_HEIGHT = useConst(35);

  const DISPLAYED_OPTIONS = useConst(10);
  const LISTBOX_PADDING = useConst(8);
  /* istanbul ignore next */
  const itemData = useMemo(() => (children as ReactElement[]) ?? [], [children]);

  const itemCount = useMemo(() => itemData.length, [itemData.length]);
  /* istanbul ignore next */
  const listHeight = useMemo(
    () =>
      itemCount > DISPLAYED_OPTIONS
        ? ITEM_HEIGHT * DISPLAYED_OPTIONS
        : ITEM_HEIGHT * itemCount,
    [DISPLAYED_OPTIONS, ITEM_HEIGHT, itemCount],
  );

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={props}>
        {/** @ts-ignore react 18 update */}
        <List
          itemData={itemData as unknown as ItemData[]}
          itemCount={itemCount}
          width="100%"
          height={listHeight + LISTBOX_PADDING}
          itemSize={() => ITEM_HEIGHT}
          innerElementType="ul"
          /** @ts-ignore react 18 update */
          outerElementType={OuterElementType}
          className="!overflow-x-hidden"
        >
          {/** @ts-ignore react 18 update */}
          {Row}
        </List>
      </OuterElementContext.Provider>
    </div>
  );
});

const PaperComponent = forwardRef<HTMLDivElement, PaperProps>(
  ({ children, ...props }, ref) => {
    const [filter, setFilter] = useContext(StateContext);
    const { t } = useTranslation();

    const filters = useMemo(
      () => [
        { value: '', label: 'All' },
        { value: 'stock', label: t('glossary.stock', { ns: 'glossary' }) },
        { value: 'etf', label: t('glossary.etf', { ns: 'glossary' }) },
        {
          value: 'mutual_fund',
          label: t('glossary.mutual-fund', { ns: 'glossary' }),
        },
      ],
      [t],
    );

    const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
      e => {
        setFilter(e.currentTarget.value);
      },
      [setFilter],
    );

    return (
      <Paper {...props} ref={ref} onMouseDown={e => e.preventDefault()}>
        <div className="flex gap-x-4 bg-page px-3 py-2">
          {Array.from(filters, ({ value, label }, index) => (
            <Button
              key={index}
              value={value}
              className="!rounded-3xl"
              type="button"
              role="tab"
              size="medium"
              variant={value === filter ? 'contained' : 'outlined'}
              onClick={onClick}
              aria-selected={value === filter}
            >
              {label}
            </Button>
          ))}
        </div>

        {children}
      </Paper>
    );
  },
);

export const AssetsSearch: FC = () => {
  const { t } = useTranslation(['common']);
  const dispatch = useAppDispatch();

  const navigate = useAppNavigate();

  const [open, setOpen] = useState(false);

  const [searchValue, setSearchValue] = useState('');

  const filterState = useState('');

  const debouncedValue = useDebounce(searchValue, 1000);

  const [buyModalState, setBuyModalState] = useState<{
    open: boolean;
    asset: AssetsSearchResp | null;
  }>({
    open: false,
    asset: null,
  });

  const currency = useAppSelector(selectAccountCurrency);
  const { sendTradeTicker } = useSendToTrader();
  const { canUseTrader } = useAdvisorRole();

  const { data, isFetching } = assetsApi.useSearchQuery(
    { name: debouncedValue, ticker: debouncedValue },
    { skip: !debouncedValue },
  );

  const options = useMemo(() => (isFetching ? [] : data ?? []), [data, isFetching]);

  const isOpen = useMemo(
    () => isFetching || (!!debouncedValue && open),
    [debouncedValue, isFetching, open],
  );

  const tickers = useAppSelector(selectComparisonTickers);

  const filterOptions = useCallback(
    (options: AssetsSearchResp[]) =>
      /* istanbul ignore next */
      isFetching
        ? []
        : options.filter(({ asset_type }) => asset_type?.includes(filterState[0])),
    [filterState, isFetching],
  );

  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const onCompare = useCallback(
    (ticker: string) => {
      dispatch(setComparisonTickers([ticker]));

      onClose();

      navigate({
        pathname: '/research',
        search: '?tab=comparison',
      });
    },
    [dispatch, navigate, onClose],
  );

  const handleOpenBuyModal = useCallback(
    (asset: AssetsSearchResp) => {
      if (!canUseTrader) return;

      setBuyModalState({ open: true, asset });
      onClose();
    },
    [canUseTrader, onClose],
  );

  const handleCloseBuyModal = useCallback(() => {
    setBuyModalState({ open: false, asset: null });
  }, []);

  const handleBuyConfirm = useCallback(
    (data: BuyOperationData, sourceAsset: SourceAsset) => {
      if (!canUseTrader) return;

      sendTradeTicker({
        operation: OperationType.BUY,
        ticker: sourceAsset.ticker,
        name: sourceAsset.name,
        volume: data.amount,
        contracts_number: data.shares,
        asset_type: sourceAsset.assetType,
        inputType: data.inputType,
      });
    },
    [canUseTrader, sendTradeTicker],
  );

  const sourceAsset: SourceAsset | null = useMemo(() => {
    if (!buyModalState.asset) return null;

    return {
      ticker: buyModalState.asset.ticker,
      name: buyModalState.asset.name,
      assetType: buyModalState.asset.asset_type || 'stock',
      investment: 0,
      quantity: 0,
      currency,
      isin: buyModalState.asset.isin ?? null,
      dfiUrl: buyModalState.asset.dfi_url ?? null,
      institution: {
        id: '',
        name: '',
      },
      accountId: '',
    };
  }, [buyModalState.asset, currency]);

  return (
    <div className="mr-auto w-full max-w-[600px]">
      <StateContext.Provider value={filterState}>
        <Autocomplete
          data-testid="assets-search"
          open={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          fullWidth
          options={options}
          onInputChange={(_, value) => {
            setSearchValue(value);
          }}
          filterOptions={filterOptions}
          disableCloseOnSelect
          selectOnFocus={false}
          noOptionsText={
            <span data-testid="search-no-results">
              {t('common.no-results', { ns: 'common' })}
            </span>
          }
          loading={isFetching}
          loadingText={
            <span data-testid="search-loading">{t('common.searching')}</span>
          }
          ListboxComponent={ListboxComponent}
          PaperComponent={PaperComponent}
          disableListWrap
          clearOnBlur={false}
          getOptionLabel={option => option.ticker}
          renderOption={(props, option, state) =>
            [
              props,
              option,
              state.index,
              onCompare,
              tickers.includes(option.ticker),
              canUseTrader ? handleOpenBuyModal : undefined,
            ] as React.ReactNode
          }
          renderInput={params => (
            <div
              className="input-ticker group relative !w-full"
              data-testid="search-ticker-input"
              ref={params.InputProps.ref}
            >
              <div className="absolute left-4 top-3">
                <AppIcon
                  icon="Search"
                  size="md"
                  color="disabled"
                  className="group-focus-within:!text-primary"
                />
              </div>
              <input
                {...params.inputProps}
                className="input-rounded pl-10"
                name="search_text"
                placeholder={t('common.search')}
                required
                type="text"
                autoComplete="off"
                data-testid="search-input"
              />
              {isFetching && (
                <CircularProgress
                  size={16}
                  className="absolute right-4 top-2"
                  data-testid="search-loading"
                />
              )}
            </div>
          )}
        />
      </StateContext.Provider>
      {canUseTrader && sourceAsset && (
        <AssetOperationModal
          open={buyModalState.open}
          onClose={handleCloseBuyModal}
          operation={OperationType.BUY}
          sourceAsset={sourceAsset}
          onBuy={handleBuyConfirm}
        />
      )}
    </div>
  );
};
